import {
  authorizationSelector,
  useOrganizationIdSelector,
} from 'modules/authorization';
import { useFeatureFlag } from 'modules/feature-flags';
import { useFunction } from 'modules/firebase';
import { subscriptionSelectors } from 'modules/subscription';
import { useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useRecoilValue } from 'recoil';
import { toast } from 'sonner';
import { buildProposalSegmentAIContext } from '../utils';

interface GenerationSnapshot {
  prompt: string;
  additionalInfo: string;
}

function normalizeValue(value?: string | null) {
  return (value || '').trim();
}

export function useAdditionalInfoAI() {
  const { control, setValue } =
    useFormContext<PublishStatusAware<Proposal>>();
  const { formatMessage, locale } = useIntl();
  const organizationId = useOrganizationIdSelector();
  const isTierOne = useRecoilValue(subscriptionSelectors.isTierOne);
  const isAIProposalGenerationEnabled = useFeatureFlag(
    'AI_PROPOSAL_GENERATION',
  );
  const generateAdditionalInfoSuggestion = useFunction<
    GenerateAdditionalInfoSuggestionData,
    GenerateAdditionalInfoSuggestionPayload
  >('generateAdditionalInfoSuggestion');

  const proposalName =
    useWatch({
      control,
      name: 'name',
    }) || '';
  const currentAdditionalInfo =
    useWatch({
      control,
      name: 'additionalInfo',
    }) || '';
  const segments =
    useWatch({
      control,
      name: 'segments',
    }) || [];

  const [isOpen, setIsOpen] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [generated, setGenerated] =
    useState<GenerateAdditionalInfoSuggestionPayload | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationSnapshot, setGenerationSnapshot] =
    useState<GenerationSnapshot | null>(null);

  const { adminRole } = useSelector(authorizationSelector.getState);

  const shouldRender =
    !!organizationId &&
    isAIProposalGenerationEnabled &&
    isTierOne &&
    adminRole === 'superAdmin';
  const proposalContext = useMemo(
    () => buildProposalSegmentAIContext(segments),
    [segments],
  );
  const currentInputSnapshot = useMemo(
    () => ({
      prompt: normalizeValue(prompt),
      additionalInfo: normalizeValue(currentAdditionalInfo),
    }),
    [currentAdditionalInfo, prompt],
  );
  const isGeneratedStale =
    !!generated &&
    !!generationSnapshot &&
    (generationSnapshot.prompt !== currentInputSnapshot.prompt ||
      generationSnapshot.additionalInfo !== currentInputSnapshot.additionalInfo);

  function handleOpen() {
    setIsOpen(true);
  }

  function resetState() {
    setPrompt('');
    setGenerated(null);
    setGenerationSnapshot(null);
    setIsOpen(false);
  }

  async function handleGenerate() {
    if (!organizationId) return;

    try {
      setIsGenerating(true);

      const { data } = await generateAdditionalInfoSuggestion({
        organizationId,
        locale,
        proposalName: normalizeValue(proposalName) || null,
        currentAdditionalInfo,
        prompt: normalizeValue(prompt) || null,
        proposalContext,
      });

      if (!data.success) {
        toast.error(
          data.message ||
            formatMessage({
              id: 'proposal-ai.additional-info.toast.generate-error',
            }),
        );
        return;
      }

      if (!data.payload) {
        toast.error(
          formatMessage({
            id: 'proposal-ai.additional-info.toast.generate-error',
          }),
        );
        return;
      }

      setGenerated(data.payload);
      setGenerationSnapshot(currentInputSnapshot);
    } catch {
      toast.error(
        formatMessage({
          id: 'proposal-ai.additional-info.toast.generate-error',
        }),
      );
    } finally {
      setIsGenerating(false);
    }
  }

  function handleApply() {
    if (!generated || isGeneratedStale) {
      return;
    }

    setValue('additionalInfo', generated.additionalInfo, {
      shouldDirty: true,
    });

    toast.success(
      formatMessage({
        id: 'proposal-ai.additional-info.toast.apply-success',
      }),
    );
    resetState();
  }

  return {
    generated,
    handleApply,
    handleGenerate,
    handleOpen,
    isGeneratedStale,
    isGenerating,
    isOpen,
    prompt,
    resetState,
    setPrompt,
    shouldRender,
  };
}
